/**
 * WebSocket Hook with Centralized Config
 * Manages WebSocket connection using configuration from server
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useConfig } from '../contexts/ConfigContext'
import { api } from '../services/enhancedApiService'
import logger from '../utils/logger'

// Helper: Get reconnect delay with exponential backoff
const getReconnectDelay = (attempt, config) => {
  if (!config?.reconnect) {
    return Math.min(1000 * Math.pow(2, attempt), 30000)
  }
  const { baseDelay, maxDelay, backoffMultiplier, jitter } = config.reconnect
  let delay = Math.min(
    baseDelay * Math.pow(backoffMultiplier, attempt),
    maxDelay
  )

  if (jitter) {
    const jitterAmount = delay * 0.25
    delay += (Math.random() * 2 - 1) * jitterAmount
  }

  return Math.floor(delay)
}

const logConnection = (message, ...args) => {
  logger.debug(`[WebSocket] ${message}`, ...args)
}

const logDebug = (message, ...args) => {
  logger.debug(`[WebSocket] ${message}`, ...args)
}

const logError = (message, ...args) => {
  logger.error(`[WebSocket Error] ${message}`, ...args)
}

export const useWebSocketWithConfig = userId => {
  const { paperTradingConfig, loading: configLoading } = useConfig()

  const [isConnected, setIsConnected] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [error, setError] = useState(null)

  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const pingIntervalRef = useRef(null)
  const slowRetryIntervalRef = useRef(null)
  const messageHandlersRef = useRef(new Map())
  const reconnectAttemptRef = useRef(0)
  const isConnectingRef = useRef(false)
  const userIdRef = useRef(userId)

  // Keep userId ref up to date
  useEffect(() => {
    userIdRef.current = userId
  }, [userId])

  /**
   * Get WebSocket URL
   */
  const getWebSocketUrl = useCallback(() => {
    if (!paperTradingConfig?.websocket?.path) return null

    const wsPath = paperTradingConfig.websocket.path
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = import.meta.env.VITE_WS_HOST || window.location.hostname
    const port = import.meta.env.VITE_WS_PORT

    // In production (HTTPS), use the same port as the website (443) via Nginx proxy
    // In development, use explicit port 8001
    if (port) {
      return `${protocol}//${host}:${port}${wsPath}`
    } else if (protocol === 'wss:') {
      // Production: WebSocket is proxied through Nginx on standard HTTPS port
      return `${protocol}//${host}${wsPath}`
    } else {
      // Development fallback: use port 8001
      return `${protocol}//${host}:8001${wsPath}`
    }
  }, [paperTradingConfig])

  /**
   * Send message to WebSocket server
   */
  const send = useCallback(message => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
      return true
    }
    return false
  }, [])

  /**
   * Register message handler for a specific message type
   */
  const on = useCallback((messageType, handler) => {
    if (!messageHandlersRef.current.has(messageType)) {
      messageHandlersRef.current.set(messageType, new Set())
    }
    messageHandlersRef.current.get(messageType).add(handler)

    // Return unsubscribe function
    return () => {
      const handlers = messageHandlersRef.current.get(messageType)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) {
          messageHandlersRef.current.delete(messageType)
        }
      }
    }
  }, [])

  /**
   * Handle incoming message
   */
  const handleMessage = useCallback(message => {
    const { type, ...data } = message

    switch (type) {
      case 'connected':
        logConnection('Connection established', data)
        break

      case 'auth_success':
        logConnection('Authentication successful', data)
        setIsAuthenticated(true)
        break

      case 'subscribed':
        logConnection('Subscribed to channels:', data.channels)
        break

      case 'unsubscribed':
        logConnection('Unsubscribed from channels:', data.channels)
        break

      case 'pong':
        // Heartbeat response
        break

      case 'error':
        logError('Server error:', data.error)
        setError(data.error)
        break

      default:
        // Route to registered message handlers
        if (messageHandlersRef.current.has(type)) {
          messageHandlersRef.current.get(type).forEach(handler => {
            try {
              handler({ type, ...data })
            } catch (err) {
              logError(`Error in message handler for ${type}:`, err)
            }
          })
        }
    }
  }, [])

  /**
   * Stop ping interval
   */
  const stopPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }, [])

  const stopSlowRetry = useCallback(() => {
    if (slowRetryIntervalRef.current) {
      clearInterval(slowRetryIntervalRef.current)
      slowRetryIntervalRef.current = null
    }
  }, [])

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      logDebug('Connection already in progress, skipping')
      return
    }

    // Don't connect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      logDebug('Already connected, skipping')
      return
    }

    // Close any existing connection first
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      logDebug('Closing pending connection')
      wsRef.current.close()
    }

    if (!paperTradingConfig || configLoading) {
      return
    }

    const url = getWebSocketUrl()
    if (!url) {
      logError('Cannot connect: WebSocket URL not available')
      return
    }

    isConnectingRef.current = true
    logConnection('Connecting to', url)

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        logConnection('Connected')
        isConnectingRef.current = false
        reconnectAttemptRef.current = 0
        setIsConnected(true)
        setError(null)
        stopSlowRetry()

        // Start heartbeat
        if (paperTradingConfig?.websocket?.heartbeatInterval) {
          stopPingInterval()
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'ping' }))
            }
          }, paperTradingConfig.websocket.heartbeatInterval)
        }
      }

      ws.onmessage = event => {
        try {
          const message = JSON.parse(event.data)
          logDebug('Received message:', message.type)
          handleMessage(message)
        } catch (err) {
          logError('Error parsing message:', err)
        }
      }

      ws.onerror = () => {
        logDebug('WebSocket error occurred (onclose will handle reconnect)')
        isConnectingRef.current = false
      }

      ws.onclose = event => {
        logConnection('Disconnected', event.code, event.reason)
        isConnectingRef.current = false
        setIsConnected(false)
        setIsAuthenticated(false)
        stopPingInterval()

        if (event.code === 1000 || !userIdRef.current) return

        // When slow retry is already running, let the interval handle the next attempt
        if (slowRetryIntervalRef.current) return

        const maxAttempts =
          paperTradingConfig?.websocket?.reconnect?.maxAttempts || 5
        if (reconnectAttemptRef.current < maxAttempts) {
          const delay = getReconnectDelay(
            reconnectAttemptRef.current,
            paperTradingConfig?.websocket
          )
          logConnection(
            `Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1}/${maxAttempts})`
          )

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptRef.current += 1
            connect()
          }, delay)
        } else {
          const slowRetryMs = 60000
          logConnection(
            `Fast reconnect exhausted. Retrying every ${slowRetryMs / 1000}s in background.`
          )

          slowRetryIntervalRef.current = setInterval(() => {
            if (!userIdRef.current) {
              stopSlowRetry()
              return
            }
            logDebug('Background reconnect attempt...')
            connect()
          }, slowRetryMs)
        }
      }
    } catch (err) {
      logError('Failed to create WebSocket:', err)
      isConnectingRef.current = false
    }
  }, [
    paperTradingConfig,
    configLoading,
    getWebSocketUrl,
    handleMessage,
    stopPingInterval,
    stopSlowRetry
  ])

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    logConnection('Disconnecting')

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    stopPingInterval()
    stopSlowRetry()
    reconnectAttemptRef.current = 0
    isConnectingRef.current = false

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected')
      wsRef.current = null
    }

    setIsConnected(false)
    setIsAuthenticated(false)
  }, [stopPingInterval, stopSlowRetry])

  /**
   * Authenticate by fetching a short-lived WS token from the API server
   */
  const authenticate = useCallback(
    async userId => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        logError('Cannot authenticate: not connected')
        return
      }

      try {
        const { data } = await api.get('/auth/ws-token')
        if (!data?.data?.token) {
          logError('No WS token received from server')
          return
        }

        logConnection('Authenticating with WS token')
        send({ type: 'auth', userId, token: data.data.token })
      } catch (err) {
        logError('Failed to obtain WS token:', err.message)
      }
    },
    [send]
  )

  /**
   * Subscribe to channels
   */
  const subscribe = useCallback(
    channels => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        logError('Cannot subscribe: not connected')
        return
      }

      if (!isAuthenticated) {
        logError('Cannot subscribe: not authenticated')
        return
      }

      logConnection('Subscribing to channels:', channels)
      send({
        type: 'subscribe',
        channels: Array.isArray(channels) ? channels : [channels]
      })
    },
    [isAuthenticated, send]
  )

  /**
   * Unsubscribe from channels
   */
  const unsubscribe = useCallback(
    channels => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        logError('Cannot unsubscribe: not connected')
        return
      }

      logConnection('Unsubscribing from channels:', channels)
      send({
        type: 'unsubscribe',
        channels: Array.isArray(channels) ? channels : [channels]
      })
    },
    [send]
  )

  // Auto-connect when config is loaded and userId is provided
  useEffect(() => {
    if (paperTradingConfig && !configLoading && userId) {
      // Small delay to allow state to settle after mount
      const timeoutId = setTimeout(() => {
        connect()
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        disconnect()
      }
    }
    // Intentionally exclude connect/disconnect from deps to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperTradingConfig, configLoading, userId])

  // Auto-authenticate when connected
  useEffect(() => {
    if (isConnected && userId && !isAuthenticated) {
      authenticate(userId)
    }
  }, [isConnected, userId, isAuthenticated, authenticate])

  return {
    isConnected,
    isAuthenticated,
    error,
    connect,
    disconnect,
    send,
    on,
    subscribe,
    unsubscribe
  }
}

export default useWebSocketWithConfig
